<script setup>
import { defineEmits, toRef, watch } from 'vue';
import SearchAirport from '@/components/search/SearchAirport.vue'
import { CalendarDaysIcon } from '@heroicons/vue/24/outline'
import Datetime from "@/components/ui/inputs/Datetime.vue";
import BaseInput from '@/components/ui/inputs/BaseInput.vue';

const props = defineProps({
  flight: Object,
  errors: Array,
  showActualSchedule: Boolean,
});

const flight = toRef(props, 'flight');
const errors = toRef(props, 'errors');
const emits = defineEmits(['update:flight']);

watch(flight, flight => {
  emit('update:flight', flight)
});
</script>

<template>
  <div class="grid grid-cols-1 text-left gap-x-8 gap-y-8 md:grid-cols-3">
    <div class="px-4 text-left sm:px-0">
      <h2 class="text-base font-semibold leading-7 text-gray-900">Détails du vol </h2>
      <!-- <p class="mt-1 text-sm leading-6 text-gray-600">Informations sur le départ et l'arrivée du vol.</p> -->
      <p class="mt-1 text-sm leading-6 text-gray-600">Ces informations sont importantes pour nous permettre de déterminer
        l'éligibilité du vol.</p>
    </div>
    <form class="rounded-md shadow-sm ring-1 ring-gray-900/5 md:col-span-2 bg-zinc-50">
      <div class="px-4 py-6 sm:p-8">
        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div class="sm:col-span-4">
            <div>
              <BaseInput v-model="flight.flightIdentifier" placeholder="Exemple : BA026" label="Numéro de vol" />
              <p v-if="errors.find(err => err.$property === 'flightIdentifier')" class="mt-1 text-sm text-left text-red-500">
                {{ errors.find(err => err.$property === 'flightIdentifier')?.$message }}
              </p>
            </div>
          </div>


          <div class="sm:col-span-12">
            <div class="grid grid-cols-2 gap-4">
              <!-- Departure Airport Search -->
              <div class="mt-2">
                <SearchAirport @update:airport="(e) => flight.departureAirportIATA = e.iata" :is-departure="true">
                  <template #label>
                    <label class="block text-sm font-medium leading-6 text-left text-gray-900">
                      Aéroport de départ <span class="italic text-black-400">*</span>
                    </label>
                  </template>
                </SearchAirport>
                <p v-if="errors.find(err => err.$property === 'departureAirportIATA')"
                  class="mt-1 text-sm text-left text-red-500">
                  {{ errors.find(err => err.$property === 'departureAirportIATA')?.$message }}
                </p>
              </div>

              <!-- Arrival Airport Search -->
              <div class="mt-2">
                <SearchAirport @update:airport="(e) => flight.arrivalAirportIATA = e.iata" :is-departure="false">
                  <template #label>
                    <label class="block text-sm font-medium leading-6 text-left text-gray-900">
                      Aéroport d'arrivée <span class="italic text-black-400">*</span>
                    </label>
                  </template>
                </SearchAirport>
                <p v-if="errors.find(err => err.$property === 'arrivalAirportIATA')"
                  class="mt-1 text-sm text-left text-red-500">
                  {{ errors.find(err => err.$property === 'arrivalAirportIATA')?.$message }}
                </p>
              </div>
            </div>
          </div>

        </div>

        <div class="grid grid-cols-2 gap-4 py-6">
          <div class="mt-2">
            <Datetime label="Horaire de départ prévu" description="Exemple: 25/07/2023 23:00" :icon="CalendarDaysIcon"
              tooltip="Horaires locales, comme indiqué sur le billet" v-model="flight.schedule.scheduledDepartureLocal" />
            <p v-if="errors.find(err => err.$property === 'scheduledDepartureLocal')" class="mt-1 text-sm text-left text-red-500">
              {{
                errors.find(err => err.$property === 'scheduledDepartureLocal')?.$message
              }}
            </p>
          </div>
          <div class="mt-2">
            <Datetime label="Horaire d'arrivée prévu" description="Exemple: 25/07/2023 23:00" :icon="CalendarDaysIcon"
              tooltip="Horaires locales, comme indiqué sur le billet" v-model="flight.schedule.scheduledArrivalLocal" />
            <p v-if="errors.find(err => err.$property === 'scheduledArrivalLocal')"
              class="mt-1 text-sm text-left text-red-500">
              {{
                errors.find(err => err.$property === 'scheduledArrivalLocal')?.$message
              }}</p>
          </div>
          <div class="mt-2" v-if="showActualSchedule">
            <Datetime label="Horaire de départ réel" description="Exemple: 25/07/2023 23:00" :icon="CalendarDaysIcon"
              tooltip="Laisser vide si inconnu" v-model="flight.schedule.actualDepartureLocal" />
            <p v-if="errors.find(err => err.$property === 'actualDepartureLocal')"
              class="mt-1 text-sm text-left text-red-500">
              {{
                errors.find(err => err.$property === 'actualDepartureLocal')?.$message
              }}</p>
          </div>
          <div class="mt-2" v-if="showActualSchedule">
            <Datetime label="Horaire d'arrivée réel" description="Exemple: 25/07/2023 23:00" :icon="CalendarDaysIcon"
              tooltip="Laisser vide si inconnu" v-model="flight.schedule.inGateArrivalLocal" />
            <p v-if="errors.find(err => err.$property === 'inGateArrivalLocal')" class="mt-1 text-sm text-left text-red-500">
              {{
                errors.find(err => err.$property === 'inGateArrivalLocal')?.$message
              }}
            </p>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
